/* CMS + market data, with the worker treated as optional.
 *
 * worker v3.8.5 serves /public-settings, /public-pages and /markets. It is
 * WRITTEN AND NOT DEPLOYED -- Bassem pastes it. Until he does, every call
 * here 404s, and every one of them falls back to a value that matches what
 * the site renders today. Nothing on screen waits for a deploy.
 *
 * That is not politeness, it is the requirement: the UI has to work before
 * the worker ships, or staging is broken for however long the paste takes.
 */

import { apiGet } from "./client";

export type Market = {
  id: number;
  code: string;            // iso_code, "EG"
  slug: string;            // "eg" -- the URL segment
  nameAr: string;
  nameEn: string;
  currency: string | null;
  currencySymbol: string | null;
  /** How an amount is written here, e.g. "{n} ج.م". Never composed by us. */
  currencyFormat: string | null;
  /* PER LOCALE, and the only thing `money.ts` reads. `label` is the currency
   * on its own («ج.م», "EGP"); `format` is the whole amount with {n} where
   * the number goes. Both come from the market row, so there is no currency
   * literal and no side-of-the-number rule anywhere in the client. */
  currencyDisplay: Record<string, { label?: string; format?: string }> | null;
  timezone: string | null;
  phoneCode: string | null;
  defaultLanguage: string | null;
  taxPercent: number | null;
  paymentGateway: string | null;
  requiresAdLicence: boolean;
  features: { projects: boolean; aiAdvisor: boolean };
  /** Whether this market can take listings. NOT whether visitors see it. */
  active: boolean;
  /** KAR-132 — what a visitor sees of it:
   *    active       offered everywhere
   *    coming_soon  offered, labelled «قريبًا»
   *    hidden       not offered at all; its URLs send the visitor elsewhere
   *  An unknown value is treated as `active`, because the failure that costs
   *  least is a market being visible when somebody meant to hide it — that is
   *  noticed and fixed in a click; the opposite silently erases a market. */
  visibility: "active" | "coming_soon" | "hidden";
};

export const VISIBILITIES = ["active", "coming_soon", "hidden"] as const;

type MarketRow = {
  id: number; iso_code: string; slug: string | null;
  name_ar: string; name_en: string;
  currency: string | null; currency_symbol: string | null;
  currency_format: string | null;
  currency_display?: Record<string, { label?: string; format?: string }> | null;
  timezone: string | null;
  phone_code: string | null; default_language: string | null;
  tax_percent: number | null; payment_gateway: string | null;
  requires_ad_licence?: boolean;
  feature_projects?: boolean; feature_ai_advisor?: boolean;
  is_active: boolean;
  visibility?: string | null;
};

export function toMarket(r: MarketRow): Market {
  return {
    id: r.id,
    code: r.iso_code,
    /* Falling back to the iso code lower-cased is safe: migration 004 seeded
     * both live markets, so this only fires for a market nobody configured. */
    slug: r.slug ?? r.iso_code.toLowerCase(),
    nameAr: r.name_ar,
    nameEn: r.name_en,
    currency: r.currency,
    currencySymbol: r.currency_symbol,
    currencyFormat: r.currency_format,
    currencyDisplay: r.currency_display ?? null,
    timezone: r.timezone,
    phoneCode: r.phone_code,
    defaultLanguage: r.default_language,
    taxPercent: r.tax_percent,
    paymentGateway: r.payment_gateway,
    requiresAdLicence: !!r.requires_ad_licence,
    features: { projects: !!r.feature_projects, aiAdvisor: !!r.feature_ai_advisor },
    active: r.is_active,
    /* Before migration 009 the column does not exist, and the honest reading
     * of the old data is the one the site already showed: an open market is
     * offered, a closed one is «قريبًا». */
    visibility: (VISIBILITIES as readonly string[]).includes(r.visibility ?? "")
      ? (r.visibility as Market["visibility"])
      : (r.is_active ? "active" : "coming_soon"),
  };
}

/** Active markets, with two fallbacks. Never returns an empty list quietly. */
export async function fetchMarkets(signal?: AbortSignal): Promise<Market[]> {
  /* READ `countries`, NOT `/markets`.
   *
   * `/markets` answers with the ACTIVE markets only — that is what it is for,
   * and `?all=1` is not a parameter it has ever honoured. This code asked for
   * it anyway and fell back to `countries` when the route 404'd, so the
   * switcher worked for exactly as long as the proxy did NOT have /markets.
   * The day v3.8.5 was deployed, /markets started answering 200 with one row,
   * the fallback stopped running, and Saudi Arabia vanished from the switcher
   * and the footer again — a feature undone by a deploy that touched nothing
   * on this page.
   *
   * `countries` is in the proxy's public-GET allowlist on every version back
   * to v3.8.0 and carries the same columns since migration 004, so it is both
   * the older door and the one that answers the question actually being
   * asked: which markets EXIST. Whether each is open is a column on the row. */
  try {
    /* EVERY market, not only the live ones. The design draws a switcher and
     * a footer market list, and both need to show a market that is coming
     * before it arrives -- a switcher that lists one country is not a
     * switcher, which is why neither control was rendering at all. `active`
     * rides along on each row so the UI can label the ones that are not open
     * yet rather than pretending they are. */
    const rows = await apiGet<MarketRow>("countries?order=id.asc", signal);
    if (rows.length) return rows.map(toMarket);
  } catch {
    /* Offline, or an origin the proxy's CORS refuses. Fall through. */
  }
  /* NO FIXTURE MARKET. This used to fall back to the dev geography so a price
   * would still carry a currency; Bassem's P0 ends that -- fixtures live in
   * src/dev and reach no build. With no market the caller gets an empty list,
   * the screen shows its error state, and nothing is rendered from data we
   * made up. A price with no currency beside it is a different claim from a
   * price; so is a price invented to have one. */
  return [];
}

import type { StoredImage } from "@/lib/imageSet";

/* ── site settings ───────────────────────────────────────────────────────
 * The shapes below mirror what migration 004 seeded. Every one has a
 * fallback equal to what the site renders today, so a missing worker, a
 * missing row or a malformed value all land in the same place: the current
 * behaviour, rather than an empty screen.
 */

export type NavItem = { key: string; visible: boolean };
export type SectionItem = { key: string; visible: boolean };

export type SiteSettings = {
  /* `image` was a single URL. It may still BE one -- an old row must not
   * blank the hero the day this ships -- but an upload now stores an
   * ImageSet so the browser can pick a width. See src/lib/imageSet.ts. */
  hero: {
    title: Record<string, string>;
    subtitle: Record<string, string>;
    image: StoredImage;
  };
  /** District id → its picture. Areas with no district id cannot have one:
   *  they are a label a lister typed, with nothing stable to key on. */
  areaImages: Record<string, StoredImage>;
  sections: SectionItem[];
  featured: { count: number; tagPriority: string[] };
  featuredAreas: number[];
  /** «المناطق المميزة» — Bassem's own order, visibility and sorting.
   *
   *  `order` and `hidden` hold DISTRICT IDS. An area the home could show but
   *  which appears in neither list simply keeps its natural position and is
   *  visible: a new district must not need a settings edit before it can be
   *  seen, and must not vanish because nobody has ranked it yet. */
  areas: {
    order: number[];
    hidden: number[];
    /** Areas with a photograph first, each group in Bassem's order. */
    photosFirst: boolean;
    /** Hide the ones with no photograph entirely. Off by default. */
    photosOnly: boolean;
    /** Show an area only once it carries a live listing.
     *
     * OFF BY DEFAULT, and that is a reversal. The home page used to derive its
     * areas FROM the listings, on the stated reasoning that "a tile which
     * opens an empty results page is worse than no tile". The effect was that
     * الرحاب, مدينتي and التجمع الأول — three of the first four areas Bassem
     * had ordered, each with a photograph he had uploaded — silently never
     * appeared, because nobody has listed in them yet.
     *
     * Bassem's ruling (2026-10-01): an area with a photograph shows. الرحاب
     * has real demand, and a marketplace that hides a district until somebody
     * lists there cannot attract the first listing. The empty results page it
     * opens is answered honestly instead, rather than prevented. */
    requireListings: boolean;
    count: number;
  };
  /** How many tiles «تصفّح حسب النوع» shows. The rest are a step away behind
   *  the last tile; disabling a type removes it everywhere, which is a
   *  different thing from not fitting on the home row. */
  typeCount: number;
  /** Bassem's home switch (migration 007, key `home.types`): show every
   *  enabled type, or only the ones with a live listing. */
  typesMode: "all" | "withListings";
  listerBand: {
    title: Record<string, string>;
    body: Record<string, string>;
    cta: Record<string, string>;
  };
  nav: {
    items: NavItem[];
    /** Per-item label overrides, by key then locale. Empty means "use ours". */
    labels: Record<string, Record<string, string>>;
    cta: { label: Record<string, string>; href: string };
  };
  contact: Record<string, unknown>;
  social: Record<string, string | null>;
  /** null means "do not show a listing count anywhere". */
  listingCountThreshold: number | null;
  /** How many photographs a listing needs before it may be published.
   *  Per market, Bassem's ruling 2026-09-26. Never below 1 on the client:
   *  the whole point of the rule is that no listing goes out without one. */
  minPhotos: number;
};

/* The order the design draws, used when nothing has been configured. */
export const DEFAULT_SECTIONS: SectionItem[] = [
  { key: "hero", visible: true },
  { key: "types", visible: true },
  { key: "areas", visible: true },
  { key: "featured", visible: true },
  { key: "advisor", visible: true },
  { key: "listerBand", visible: true },
  { key: "plans", visible: true },
];

/* Featured > Hot > New > untagged, then the backend's order. Bassem's ruling:
 * a paid "Featured" listing must never tie with "New", which is what happens
 * when the sort only asks whether a tag exists. Kept as one named constant so
 * there is a single place to change it -- and logged in V3.9.0-SPEC as a
 * setting that should become data rather than code. */
export const TAG_PRIORITY = ["Featured", "Hot", "New"] as const;

export const FALLBACK_SETTINGS: SiteSettings = {
  hero: { title: {}, subtitle: {}, image: null },
  areaImages: {},
  sections: DEFAULT_SECTIONS,
  featured: { count: 8, tagPriority: [...TAG_PRIORITY] },
  featuredAreas: [],
  areas: { order: [], hidden: [], photosFirst: true, photosOnly: false, requireListings: false, count: 8 },
  /* The design draws eight. */
  typeCount: 8,
  typesMode: "all",
  listerBand: { title: {}, body: {}, cta: {} },
  nav: {
    items: [
      { key: "buy", visible: true },
      { key: "rent", visible: true },
      /* projects has no backend; it is false here and the admin toggle is
       * disabled, so it cannot be switched on by accident. */
      { key: "projects", visible: false },
      { key: "advisor", visible: true },
      { key: "plans", visible: true },
    ],
    labels: {},
    cta: { label: {}, href: "/list-property" },
  },
  contact: {},
  social: {},
  listingCountThreshold: null,
  minPhotos: 1,
};

/* A stored setting, over its default.
 *
 * A PARTIAL OBJECT MERGES; it does not replace. This used to hand the stored
 * value back whole, which meant a row holding `{image: …}` and no `title`
 * became the entire hero setting — and the page then read
 * `settings.hero.title[locale]` and CRASHED. A white screen, from one saved
 * field. The header above this section promises that a malformed value lands
 * on the current behaviour rather than an empty screen; for a missing KEY
 * that was true, and for a partial OBJECT it was not.
 *
 * Arrays and primitives still replace outright: a list of sections is the
 * whole list, and merging one into its default would resurrect a section an
 * admin deliberately removed.
 *
 * One level deep is enough. Every setting here is flat or one nested map, and
 * a deep merge would make a stored `{}` indistinguishable from "unset" at
 * every level — which is how you get a field nobody can ever clear. */
const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const pick = <T,>(raw: Record<string, unknown>, key: string, fallback: T): T => {
  const v = raw[key];
  if (v === undefined || v === null) return fallback;
  if (isPlainObject(v) && isPlainObject(fallback)) return { ...fallback, ...v } as T;
  return v as T;
};

/** Whitelisted settings for one market. Never throws; never blocks a render. */
export async function fetchSettings(
  marketSlug: string | null, signal?: AbortSignal,
): Promise<SiteSettings> {
  let raw: Record<string, unknown> = {};
  try {
    const rows = await apiGet<Record<string, unknown>>(
      `public-settings${marketSlug ? `?market=${encodeURIComponent(marketSlug)}` : ""}`, signal);
    raw = (rows[0] ?? {}) as Record<string, unknown>;
  } catch {
    return FALLBACK_SETTINGS;
  }
  const f = FALLBACK_SETTINGS;
  return {
    hero: pick(raw, "home.hero", f.hero),
    areaImages: pick(raw, "home.areaImages", f.areaImages),
    sections: pick(raw, "home.sections", f.sections),
    featured: pick(raw, "home.featured", f.featured),
    featuredAreas: pick(raw, "home.featuredAreas", f.featuredAreas),
    /* Merged over the defaults one level deep by `pick`, so a row holding only
       `{count: 4}` keeps photosFirst true rather than turning it off. The
       numbers are still clamped here, because a stored 0 would empty the
       section and a stored list of the wrong type would break the sort. */
    areas: (() => {
      const a = pick(raw, "home.areas", f.areas);
      const ids = (v: unknown) => (Array.isArray(v) ? v.map(Number).filter(Number.isFinite) : []);
      return {
        order: ids(a.order),
        hidden: ids(a.hidden),
        photosFirst: a.photosFirst !== false,
        photosOnly: a.photosOnly === true,
        /* Explicit `=== true`, so an older settings row that predates this key
         * reads as FALSE and the area shows. Defaulting the other way would
         * re-hide الرحاب on every market whose settings have not been re-saved. */
        requireListings: a.requireListings === true,
        count: Math.max(1, Number(a.count) || 8),
      };
    })(),
    typeCount: pick(raw, "home.typeCount", f.typeCount),
    /* `home.types` is stored as { mode }, so the flag is read out of it
       rather than kept as a second row that could disagree. */
    typesMode: ((raw["home.types"] as { mode?: string } | undefined)?.mode === "withListings")
      ? "withListings" : "all",
    listerBand: pick(raw, "home.listerBand", f.listerBand),
    nav: {
      items: pick(raw, "nav.items", f.nav.items),
      labels: pick(raw, "nav.labels", f.nav.labels),
      cta: pick(raw, "nav.cta", f.nav.cta),
    },
    contact: pick(raw, "site.contact", f.contact),
    social: pick(raw, "site.social", f.social),
    listingCountThreshold: pick(raw, "site.listingCountThreshold", f.listingCountThreshold),
    /* Clamped, not trusted: a 0 or a negative in the row would quietly turn
       the rule off, which is the one outcome the ruling forbids. */
    minPhotos: Math.max(1, Number(pick(raw, "listings.minPhotos", f.minPhotos)) || 1),
  };
}

/** Which content pages exist, so a link is only drawn for one that does. */
export async function fetchPageIndex(
  locale: string, marketSlug: string | null, signal?: AbortSignal,
): Promise<{ slug: string; title: string }[]> {
  try {
    return await apiGet<{ slug: string; title: string }>(
      `public-pages?locale=${encodeURIComponent(locale)}`
      + (marketSlug ? `&market=${encodeURIComponent(marketSlug)}` : ""), signal);
  } catch {
    /* v3.8.5 is not deployed, or nothing is published. Either way the answer
     * the caller needs is the same: no page is known to exist, so no link. */
    return [];
  }
}

/** A published content page, or null. Drafts are invisible by construction. */
export async function fetchPage(
  slug: string, locale: string, marketSlug: string | null, signal?: AbortSignal,
) {
  try {
    const rows = await apiGet<{ title: string; body: string; meta_description: string }>(
      `public-pages?slug=${encodeURIComponent(slug)}&locale=${encodeURIComponent(locale)}`
      + (marketSlug ? `&market=${encodeURIComponent(marketSlug)}` : ""), signal);
    return rows[0] ?? null;
  } catch {
    return null;
  }
}

/* ── the lister, as the public may see them ──────────────────────────────
 * Served by worker v3.8.5's /public-profile, which is a five-column
 * allowlist: no email, no phone, no plan. NOT DEPLOYED YET, so this returns
 * null today and every surface that uses it hides itself rather than drawing
 * an empty card with a blank name in it.
 */

export type PublicLister = {
  id: string;
  name: string | null;
  /** "agent", "owner", … as the users table spells it. Mapped to copy in the UI. */
  role: string | null;
  verified: boolean;
  /** ISO date. Rendered as a year, which is all the design shows. */
  joined: string | null;
};

export async function fetchLister(
  id: string, signal?: AbortSignal,
): Promise<PublicLister | null> {
  if (!id) return null;
  try {
    const rows = await apiGet<{
      id: string; name: string | null; role: string | null;
      verified: boolean | null; joined: string | null;
    }>(`public-profile?id=${encodeURIComponent(id)}`, signal);
    const r = rows[0];
    if (!r) return null;
    return { id: r.id, name: r.name, role: r.role, verified: !!r.verified, joined: r.joined };
  } catch {
    return null;
  }
}

/* ── copy overrides ──────────────────────────────────────────────────────
 * Served by worker v3.8.5's /public-texts as { ar: {...}, en: {...} } — BOTH
 * locales in one answer, because the market is held above the locale and a
 * visitor switching language must not wait on a second request to see the
 * words change.
 *
 * NOT DEPLOYED YET, so this returns {} today and every screen renders the
 * designed wording. That is the whole contract: the i18n files are the
 * defaults, this is only ever the overrides.
 */
export async function fetchTexts(
  marketSlug: string | null, signal?: AbortSignal,
): Promise<Record<string, Record<string, string>>> {
  try {
    const rows = await apiGet<Record<string, Record<string, string>>>(
      `public-texts${marketSlug ? `?market=${encodeURIComponent(marketSlug)}` : ""}`, signal);
    const one = rows[0];
    return one && typeof one === "object" ? one : {};
  } catch {
    return {};
  }
}


/* ── the property types this market offers ───────────────────────────────
 *
 * ONE LIST, IN ADMIN'S ORDER. `/public-types` answers with the enabled,
 * unarchived types for a market, already ordered, so the home tiles, the
 * search filter and the wizard cannot disagree about what exists or what
 * order it comes in.
 *
 * THE FALLBACK IS REAL DATA, NOT A GUESS. The route arrived in proxy v3.8.6.
 * Against anything older it 401s, and the caller is handed the standard seed
 * list narrowed to the types that ACTUALLY HAVE A LISTING in this market --
 * which is exactly what the home page derived for itself before this existed.
 * Nothing is invented: every type in that answer has a real listing behind it.
 * What is lost until the proxy is deployed is Bassem's ORDER and his
 * per-market switches, not the honesty of the list.
 */
export type SiteType = {
  id: number | null;
  slug: string;
  /** The value stored in `listings.type`, which is what a filter matches on. */
  legacy: string | null;
  nameAr: string;
  nameEn: string;
  icon: string;
  category: string;
  fields: string[];
  listings: number;
  activeListings: number;
};

type PublicTypeRow = {
  id?: number; slug?: string; legacy?: string | null;
  nameAr?: string; nameEn?: string; icon?: string; category?: string;
  fields?: string[]; listings?: number; activeListings?: number;
};

export async function fetchTypes(
  marketSlug: string | null,
  /** Types that have at least one live listing, for Bassem's home switch. */
  withListingsOnly: boolean,
  signal?: AbortSignal,
): Promise<SiteType[] | null> {
  try {
    const rows = await apiGet<PublicTypeRow>(
      `public-types?${marketSlug ? `market=${encodeURIComponent(marketSlug)}&` : ""}`
      + `${withListingsOnly ? "mode=withListings" : ""}`, signal);
    if (Array.isArray(rows) && rows.length) {
      return rows.map((r) => ({
        id: r.id ?? null,
        slug: String(r.slug ?? ""),
        legacy: r.legacy ?? null,
        nameAr: String(r.nameAr ?? ""),
        nameEn: String(r.nameEn ?? ""),
        icon: String(r.icon ?? ""),
        category: String(r.category ?? ""),
        fields: Array.isArray(r.fields) ? r.fields : [],
        listings: Number(r.listings ?? 0),
        activeListings: Number(r.activeListings ?? 0),
      }));
    }
  } catch {
    /* Older proxy, or offline. The caller falls back. */
  }
  return null;
}
